import { IntlShape } from "react-intl";
import { shouldEnableFsdVouchers, shouldEnableCcVouchers } from "./featureFlagUtil";
import type { RealPaymentType } from "../stores/CheckoutTypes";
import type {
	CardholderData,
	CheckoutData,
	FullPayment,
	PaymentSelection,
	PaymentType,
	ServiceTypeSelection,
} from "../model/CheckoutData";
import { MinimumOrderValue, Price } from "../model/Prices";

const getKey = (name: string): string => {
	return "ORC_PAYMENT_" + name;
};

export const MAX_DELIVERY_DATE_FUTURE = 90;
// migrate to createPaymentKey in BOC-61772
export function createRealPaymentKey(type: { paymentType?: string | null; paymentAllowance: string }): string {
	if (type.paymentType) {
		return getKey(`${type.paymentAllowance}_${type.paymentType}`);
	}

	return getKey(type.paymentAllowance);
}
export function createPaymentKey(allowance: string, type?: string | null): string {
	return createRealPaymentKey({
		paymentAllowance: allowance,
		paymentType: type,
	});
}
export function createRealPaymentLabel(
	type: {
		paymentType?: string | null;
		paymentAllowance: string;
	},
	intl: IntlShape,
): string {
	return intl.formatMessage({
		id: createRealPaymentKey(type),
	});
}
export function isSamePaymentType(x: PaymentType, y: PaymentType) {
	return x.paymentAllowance === y.paymentAllowance && x.paymentType === y.paymentType;
}

export function isMetroCreditAllowance(paymentAllowance: string | null | undefined) {
	return paymentAllowance === "CREDIT";
}
export function getMetroCredit(): PaymentType {
	return {
		paymentAllowance: "CREDIT",
		paymentType: null,
	};
}
export function findMatchingRealPaymentType(
	targetMethod:
		| {
				paymentAllowance: string;
				paymentType: string | null | undefined;
		  }
		| null
		| undefined,
	availableTypes: Array<RealPaymentType>,
): RealPaymentType | null | undefined {
	return availableTypes.find(
		(payment) =>
			targetMethod &&
			(payment.paymentType === targetMethod.paymentType || // if one is null or undefined and the other is "" consider it a match
				(!payment.paymentType && !targetMethod.paymentType)) &&
			payment.paymentAllowance === targetMethod.paymentAllowance,
	);
}
export function findPayment(
	paymentSelection: PaymentSelection,
	predicate: (arg0: FullPayment) => boolean,
): [boolean, FullPayment] | null | undefined {
	const mainPayment = paymentSelection.mainPayment;

	if (predicate(mainPayment)) {
		return [true, mainPayment];
	}

	const complementaryPayment = paymentSelection.complementaryPayment;

	if (complementaryPayment && predicate(complementaryPayment)) {
		return [false, complementaryPayment];
	}

	return null;
}
export function isFsdOnlyCheckout(checkoutData: CheckoutData | null | undefined): boolean {
	return (
		!!checkoutData && Object.values(checkoutData.groupHeaders).every((header) => header?.fulfillmentType === "FSD")
	);
}
export function containsFsdHeader(checkoutData: CheckoutData | null | undefined): boolean {
	return (
		!!checkoutData && Object.values(checkoutData.groupHeaders).some((header) => header?.fulfillmentType === "FSD")
	);
}
export function containsClickAndCollectHeader(checkoutData: CheckoutData | null | undefined): boolean {
	return (
		!!checkoutData &&
		Object.values(checkoutData.groupHeaders).some((header) => header?.fulfillmentType === "CLICK_AND_COLLECT")
	);
}
export function concatFsdConfirmationEmails(checkoutData: CheckoutData | null | undefined): string | null | undefined {
	if (!checkoutData) {
		return null;
	}

	if (
		!checkoutData.header.sendOrderConfirmationToCustomer &&
		!checkoutData.header.sendOrderConfirmationToCardholder
	) {
		return null;
	}

	if (!isFsdOnlyCheckout(checkoutData)) {
		return checkoutData.header.emailAddress ?? "";
	}

	const customerEmail = checkoutData.header.sendOrderConfirmationToCustomer && checkoutData.customerData.email;
	let cardholderEmail;

	if (checkoutData.header.sendOrderConfirmationToCardholder) {
		const cardholderData: CardholderData | null | undefined =
			checkoutData.customerData.cardholderById[checkoutData.context.cardholderId];
		cardholderEmail = !!cardholderData && cardholderData.email;
	}

	if (!customerEmail && !cardholderEmail) {
		return checkoutData.header.emailAddress ?? null;
	} else {
		return [...new Set([customerEmail, cardholderEmail])].filter(Boolean).join(",");
	}
}
export function areFsdVouchersEnabled(): boolean {
	return shouldEnableFsdVouchers();
}
export function areCcVouchersEnabled(): boolean {
	return shouldEnableCcVouchers();
}
export function serviceTypeSelectionEquals(
	a: ServiceTypeSelection | null | undefined,
	b: ServiceTypeSelection | null | undefined,
): boolean {
	if (a === b) {
		return true;
	}

	if (a && b) {
		return a.preferredServiceType === b.preferredServiceType;
	}

	return false;
}

export function isMovFeeIgnoredWithOrdersOnSameDeliveryDate(
	movField: MinimumOrderValue | null | undefined,
	movFeeCharged: Price | null | undefined,
): boolean {
	if (!movField?.minOrderValue || !movField?.feeAmount) return false;

	return movField.feeIgnored && !movField.feeApplied && (!movFeeCharged?.gross || !movFeeCharged?.net);
}
